<script setup>
import { computed, ref, useTemplateRef, watch } from 'vue'
import QvHiddenEsgMaps from '@/components/quick-valuation/QvHiddenEsgMaps.vue'
import QvHiddenMaps from '@/components/quick-valuation/QvHiddenMaps.vue'
import { useShortReportDownload } from '@/composables/useShortReportDownload'

/**
 * Reusable off-screen map capture host (RED-212).
 *
 * Owns the lifecycle that QuickValuationResult.vue used to manage inline:
 *   1. Mounts QvHiddenMaps (4 location maps) + QvHiddenEsgMaps (8 ESG maps)
 *      off-screen so OpenLayers can render their canvases.
 *   2. Exposes `captureAll(onProgress?)` returning the combined payload
 *      `{ mikro, makro, kataster, infra, esg: { ... } }` — same shape that
 *      `useShortReportDownload.appendMapsToFormData` consumes.
 *   3. Optionally warm-captures on idle (when `warmCapture` is true) and POSTs
 *      to `/quick-valuation/{id}/maps` so subsequent exports — including from
 *      QuickValuationHistory.vue — find the cache populated.
 *
 * Used by both QuickValuationResult.vue (live result page) and
 * QuickValuationHistory.vue (re-export from history). Both pass through
 * `useQuickValuationHydration` first so the Pinia stores driving the hidden
 * maps (detailStore.ehrGeometry, landBoardStore.landBoard) are populated.
 */

const props = defineProps({
  evaluationId: {
    type: [Number, String],
    required: true,
  },
  ehrData: {
    type: Object,
    default: () => ({}),
  },
  warmCapture: {
    type: Boolean,
    default: false,
  },
  landBoardBbox: {
    type: Array,
    default: null,
  },
})

const hiddenMapsRef = useTemplateRef('hiddenMapsRef')
const hiddenEsgMapsRef = useTemplateRef('hiddenEsgMapsRef')

const hasGeometry = computed(() => Array.isArray(props.ehrData?.geometry) && props.ehrData.geometry.length > 0)

// ---- Mount-buffer settle window ----
//
// RED-212: `MapComponent.exportMap` now gates on real tile-source state via
// `waitForMapLoaded` (Map `loadstart`/`loadend` + `rendercomplete`, 8s
// ceiling), so the per-map export blocks until the visible WMS tiles have
// actually landed. The previous 3s wall-clock window from `dataReadyAt` is no
// longer the readiness gate.
//
// We still want a small buffer so `captureChildren` doesn't fire during the OL
// `onMounted` chain on the 12 hidden maps — they need a moment to register
// their layer groups, kick off their first tile requests, and resolve any
// `fitTo` animation queued by `updateOverlay`/`updateBbox`. 500ms is observed
// to cover that window comfortably; captures that arrive after the
// warm-capture path pass through instantly.
const SETTLE_MS = 500
const dataReadyAt = ref(null)
const warmCapturePromise = ref(null)

const isDataReady = computed(() =>
  hiddenMapsRef.value
    && hiddenEsgMapsRef.value
    && hasGeometry.value
    && Array.isArray(props.landBoardBbox)
    && props.landBoardBbox.length > 0,
)

watch(isDataReady, (ready) => {
  if (ready && dataReadyAt.value === null) {
    dataReadyAt.value = Date.now()
  }
}, { immediate: true })

const waitForSettle = async () => {
  if (dataReadyAt.value === null) return
  const elapsed = Date.now() - dataReadyAt.value
  const remaining = SETTLE_MS - elapsed
  if (remaining > 0) {
    await new Promise((resolve) => setTimeout(resolve, remaining))
  }
}

/**
 * Drive the children's exports. No settle/serialization logic here — the
 * public `captureAll` and the warm-capture flow each apply their own waits
 * before calling this. Splitting them avoids the obvious deadlock where the
 * warm-capture's own captureAll would await the warm-capture promise we just
 * latched it onto.
 */
const captureChildren = async (onProgress) => {
  const locTotal = hiddenMapsRef.value?.total ?? 0
  const esgTotal = hiddenEsgMapsRef.value?.total ?? 0
  const total = locTotal + esgTotal

  let completed = 0
  const forward = ({ label }) => {
    completed += 1
    if (typeof onProgress === 'function') {
      onProgress({ completed, total, label })
    }
  }

  const locationMapsPromise = hiddenMapsRef.value?.captureAll
    ? hiddenMapsRef.value.captureAll(forward)
    : Promise.resolve({})
  const esgMapsPromise = hiddenEsgMapsRef.value?.captureAll
    ? hiddenEsgMapsRef.value.captureAll(forward)
    : Promise.resolve({})

  const [locationMaps, esgMaps] = await Promise.all([locationMapsPromise, esgMapsPromise])
  return { ...locationMaps, esg: esgMaps }
}

/**
 * Combined capture across location + ESG maps. The `onProgress` callback is
 * invoked after each individual map settles with `{ completed, total, label }`.
 * Total is fixed at 12 (4 location + 8 ESG); children expose their per-host
 * totals via the `total` defineExpose so this stays correct if the counts ever
 * shift.
 *
 * Concurrency contract: if a warm-capture is already in flight, defer to it
 * (running two captures in parallel on the same OL maps would race on the
 * canvas and on the shared landBoard `features` array). Otherwise apply the
 * mount-buffer wait so the hidden maps have a moment to mount and start
 * their first tile requests; per-map tile readiness is enforced inside
 * `MapComponent.exportMap` via `waitForMapLoaded`.
 */
const captureAll = async (onProgress) => {
  if (warmCapturePromise.value) {
    try {
      await warmCapturePromise.value
    } catch {
      // Warm-capture errors are logged by its own catch; we just need to know
      // it's done so we can run cleanly afterwards.
    }
    return captureChildren(onProgress)
  }

  await waitForSettle()
  return captureChildren(onProgress)
}

defineExpose({ captureAll })

// ---- Warm-capture on idle (opt-in) ----
//
// Lifted from QuickValuationResult.vue's pre-RED-212 lifecycle. Once refs +
// geometry + bbox are all present, schedule one capture-and-upload on the
// next idle callback. Per-map tile readiness is enforced by `waitForMapLoaded`
// inside `MapComponent.exportMap`, so the 2.5s pre-delay below is purely an
// idle-scheduling cushion — not a correctness gate. Failures are silent
// (console.warn) since this is a background prefetch and the user's UX
// doesn't depend on it.

const { saveMaps } = useShortReportDownload()
const warmCaptureStarted = ref(false)

const scheduleWarmCapture = () => {
  if (warmCaptureStarted.value) return
  warmCaptureStarted.value = true

  const run = async () => {
    try {
      // captureChildren — not captureAll — to avoid deadlocking on our own
      // warmCapturePromise. Per-map tile readiness is handled inside
      // MapComponent.exportMap → waitForMapLoaded.
      const maps = await captureChildren()
      await saveMaps({ evaluationId: props.evaluationId, maps })
    } catch (err) {
      console.warn('[QvMapCaptureHost] warm-capture failed', err)
    } finally {
      warmCapturePromise.value = null
    }
  }

  const delay = 2500
  const start = () => {
    // Latch the promise so concurrent captureAll() callers can await it
    // instead of running their own export against the same OL canvases.
    warmCapturePromise.value = run()
  }

  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    setTimeout(() => window.requestIdleCallback(start, { timeout: 5000 }), delay)
  } else {
    setTimeout(start, delay)
  }
}

watch(
  () => [
    props.warmCapture,
    hiddenMapsRef.value,
    hiddenEsgMapsRef.value,
    hasGeometry.value,
    props.landBoardBbox,
  ],
  ([enabled, locRef, esgRef, geomReady, bbox]) => {
    if (enabled && locRef && esgRef && geomReady && bbox) {
      scheduleWarmCapture()
    }
  },
  { immediate: true },
)
</script>

<template>
  <template v-if="hasGeometry">
    <QvHiddenMaps
      ref="hiddenMapsRef"
      :ehr-data="ehrData"
      :ehr-id="evaluationId"
    />
    <QvHiddenEsgMaps
      ref="hiddenEsgMapsRef"
      :ehr-data="ehrData"
      :ehr-id="evaluationId"
    />
  </template>
</template>
